import { Fragment, useCallback, useMemo, useState } from 'react';

import { useAuth } from '../../api.ts';
import {
  FloatingToolbar,
  GroupSkillsIcon,
  ShowDepartedIcon,
  ShowLibrariesIcon,
  ToolbarMultiSelect,
  ToolbarSeparator,
  ToolbarToggle,
} from '../components/FloatingToolbar.tsx';
import { MatrixCell } from '../components/MatrixCell.tsx';
import { MatrixScrollBox } from '../components/MatrixScrollBox.tsx';
import { EmptyView, ErrorView, SignInView, Skeleton } from '../components/Skeleton.tsx';
import { useTooltipHandlers } from '../components/Tooltip.tsx';
import {
  ConnectorCell,
  GroupedCompLabel,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../components/TruncatedLabel.tsx';
import {
  RATING_LABELS,
  buildIntroductionIndex,
  buildOwnershipIndex,
  buildRatingIndex,
  compRowHeight,
  indexSkills,
  isAgent,
  isCompactCatalog,
  isInstitutional,
  levelClass,
} from '../data-views.ts';
import type { Engineer, RatingValue } from '../data-views.ts';
import { sortByClientDistance } from '../derived-context-order.ts';
import { useTalentView } from '../hooks/useViews.ts';
import { usePersonFilter } from '../person-filter.ts';
import { useTeamFilter } from '../team-filter.ts';
import { useTechFilter } from '../tech-filter.ts';

import { TalentProfileMap } from './TalentProfileMap.tsx';

const SUBTITLE = 'Team proficiency across the platform.';

export function TalentView() {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return <Skeleton title='Talent' subtitle={SUBTITLE} rows={10} />;
  }
  if (auth.status === 'signed-out') {
    return <SignInView title='Talent' />;
  }
  return <TalentLoader />;
}

function TalentLoader() {
  const { data: view, isLoading, error } = useTalentView();
  if (error) {
    return <ErrorView title='Talent' error={error} />;
  }
  if (isLoading || !view) {
    return <Skeleton title='Talent' subtitle={SUBTITLE} rows={10} />;
  }
  if (view.derivedContexts.length === 0 || view.engineers.length === 0) {
    return <EmptyView title='Talent' subtitle={SUBTITLE} />;
  }
  return (
    <TalentBody
      derivedContexts={view.derivedContexts}
      businessFunctions={view.business_functions}
      allEngineers={view.engineers}
      ratings={view.ratings}
      ownership={view.ownership}
      introductions={view.introductions}
    />
  );
}

// "Christine Miao" → "Christine M.". Single-word names pass through.
function formatShortName(display: string): string {
  const parts = display.trim().split(/\s+/);
  if (parts.length < 2) return display;
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  const lastInitial = last[0]?.toUpperCase() ?? '';
  return lastInitial ? `${first} ${lastInitial}.` : first;
}

/**
 * Engineer column header. Renders the short name (rotated by CSS) and
 * surfaces full identity context on hover: full name, git emails, github
 * logins, status. The tooltip is the only place the unabbreviated name
 * + linked accounts are visible without leaving the matrix.
 *
 * `zIndex` descends left→right (first column highest) so each rotated
 * name — which overflows up-and-right into the next column — paints over
 * that column's opaque header background instead of being clipped by it.
 */
function EngineerColHeader({
  engineer,
  zIndex,
  onSelect,
}: {
  engineer: Engineer;
  zIndex: number;
  /** When set, the header is clickable and drills into this engineer's
   *  single-person Talent Profile (same as picking them in the person filter). */
  onSelect?: (engineerId: string) => void;
}) {
  const emails = engineer.git_emails ?? [];
  const logins = engineer.github_logins ?? [];
  const bodyLines: string[] = [];
  for (const e of emails) bodyLines.push(e);
  for (const l of logins) bodyLines.push(`@${l}`);
  const sub =
    engineer.status === 'departed'
      ? 'Departed'
      : engineer.kind === 'agent'
        ? 'Agent'
        : undefined;
  const tooltip = {
    title: engineer.display_name,
    body: bodyLines.length > 0 ? bodyLines.join('\n') : undefined,
    sub: onSelect ? (sub ? `${sub} · View profile` : 'View profile') : sub,
  };
  const handlers = useTooltipHandlers(tooltip);
  const clickable = onSelect != null;
  return (
    <div
      className={`snap-grid__col-header${clickable ? ' snap-grid__col-header--clickable' : ''}`}
      style={{ zIndex }}
      {...handlers}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `View ${engineer.display_name}'s talent profile` : undefined}
      onClick={clickable ? () => onSelect?.(engineer.id) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(engineer.id);
              }
            }
          : undefined
      }
    >
      <strong>{formatShortName(engineer.display_name)}</strong>
    </div>
  );
}

function TalentBody({
  derivedContexts,
  businessFunctions,
  allEngineers,
  ratings,
  ownership,
  introductions,
}: {
  derivedContexts: import('../data-views.ts').DerivedContext[];
  businessFunctions: import('../data-views.ts').BusinessFunction[];
  allEngineers: Engineer[];
  ratings: import('../data-views.ts').Rating[];
  ownership: import('../data-views.ts').Ownership[];
  introductions: import('../data-views.ts').Introduction[];
}) {
  // Default to grouped (one row per derivedContext) — matches Timeline /
  // Roadmap; the toolbar toggle expands to per-skill rows.
  const [grouped, setGrouped] = useState(true);
  // Per-derivedContext skill expand/collapse — click a derivedContext's connector line
  // to list its skills (or fold them back), the same affordance the /setup
  // editor uses. `grouped` is the baseline; `expandOverrides` holds the ids
  // that differ from it, so "Group Skills" reads as collapse/expand-all while
  // individual connectors fine-tune single derivedContexts.
  const [expandOverrides, setExpandOverrides] = useState<Set<string>>(new Set());
  const isCompExpanded = useCallback(
    (id: string) => (expandOverrides.has(id) ? grouped : !grouped),
    [expandOverrides, grouped],
  );
  const toggleCompExpanded = useCallback((id: string) => {
    setExpandOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const setAllGrouped = useCallback((g: boolean) => {
    setGrouped(g);
    setExpandOverrides(new Set());
  }, []);
  const techFilter = useTechFilter();
  const teamFilter = useTeamFilter();
  const personFilter = usePersonFilter(allEngineers);
  // Narrow which engineers appear as columns to the selected team + people.
  const team = useMemo(
    () => personFilter.filter(teamFilter.filter(allEngineers)),
    [personFilter, teamFilter, allEngineers]
  );
  // Default to active-only: departed engineers hide unless the user
  // explicitly opts in. Keeps the matrix focused on current talent and
  // avoids 30+ greyed-out columns dominating the view for orgs with
  // long contributor tails. Toolbar toggle reveals them.
  const [showDeparted, setShowDeparted] = useState(false);

  // TalentView doesn't render function columns; it only needs the
  // function COUNT to keep the skill-column width aligned with
  // Resilience (see gridStyle below). Derive from derivedContext back-
  // edges so we don't need to fetch /views/resilience or business_
  // functions separately.
  const fnCount = useMemo(() => {
    const set = new Set<string>();
    for (const c of derivedContexts) for (const f of c.functions) set.add(f);
    return set.size;
  }, [derivedContexts]);

  const skillsById = useMemo(() => indexSkills(derivedContexts), [derivedContexts]);
  const ratingIndex = useMemo(
    () => buildRatingIndex(skillsById, team, ratings),
    [skillsById, team, ratings]
  );
  const ownershipIndex = useMemo(() => buildOwnershipIndex(ownership), [ownership]);
  const introductionIndex = useMemo(() => buildIntroductionIndex(introductions), [introductions]);

  const visibleComps = useMemo(() => {
    // Client-distance order (client-facing first) is the default row order.
    return sortByClientDistance(derivedContexts)
      .map(comp => ({
        ...comp,
        skills: comp.skills.filter(techFilter.visible),
      }))
      .filter(comp => comp.skills.length > 0);
  }, [derivedContexts, techFilter]);

  // Up to three segments, each separated by a fixed-width gap track:
  // active humans, then active agents, then everyone departed. Agents get
  // their OWN segment (rather than sitting flush against the humans) so the
  // gap track + the holographic ::before overlay together read clearly as
  // "these are bots." Empty segments are dropped — emitting an empty
  // segment expands to `repeat(0, 36px)` which is invalid CSS and tanks the
  // whole grid-template-columns (the cause of the "names stacked
  // vertically" regression when every contributor is departed). Gap track
  // is only emitted between consecutive non-empty segments.
  const peopleSegments: Engineer[][] = useMemo(() => {
    const humans = team.filter(e => !isAgent(e) && e.status === 'active');
    const activeAgents = team.filter(e => isAgent(e) && e.status === 'active');
    const departed = showDeparted ? team.filter(e => e.status === 'departed') : [];
    const segments: Engineer[][] = [];
    if (humans.length > 0) segments.push(humans);
    if (activeAgents.length > 0) segments.push(activeAgents);
    if (departed.length > 0) segments.push(departed);
    return segments;
  }, [team, showDeparted]);

  // Descending z-index per engineer column (leftmost = highest) so each
  // rotated header name paints over the next column's opaque background.
  // Capped below the frozen corner (z 200, see tokens.css).
  const colZ = useMemo(() => {
    const total = peopleSegments.reduce((n, s) => n + s.length, 0);
    const m = new Map<string, number>();
    let i = 0;
    for (const seg of peopleSegments) {
      for (const p of seg) {
        m.set(p.id, total - i);
        i += 1;
      }
    }
    return m;
  }, [peopleSegments]);

  // Align the team's leftmost cell with where the Resilience matrix begins,
  // without stretching cells. Resilience's skill column is `1fr` shared with
  // N_fn `1fr` function columns, so it ends up at freeSpace / (1 + N_fn).
  // Here the skill column stays `1fr` and we add a trailing `N_fn fr` filler
  // after the (fixed 36px) people cells — total fr = 1 + N_fn, so skill_col
  // = freeSpace / (1 + N_fn), the same as resilience. The filler eats the
  // remaining space so cells don't stretch.
  const gridStyle = useMemo(() => {
    const trailingFr = fnCount > 0 ? fnCount : 1;
    const cols: string[] = ['130px', '42px', '6px', '140px'];
    peopleSegments.forEach((seg, i) => {
      if (i > 0) cols.push('24px');
      cols.push(`repeat(${seg.length}, 36px)`);
    });
    cols.push(`${trailingFr}fr`);
    const base: Record<string, string> = {
      gridTemplateColumns: cols.join(' '),
    };
    // Row 1 pinned to 80px so the slanted-name column header
    // (height: 80px) fits inside its row track. Without this, Talent's
    // `grid-auto-rows: 38px` shrinks row 1 and the engineer-header cell
    // visually invades the first skill row below. Keep in sync with the
    // `.snap-grid--talent .snap-grid__col-header` height in tokens.css.
    // One track per rendered row after the 80px header: a collapsed
    // derivedContext is a single band sized to its skill count (so dense comps
    // read heavier); an expanded one lists a 38px row per skill (matching the
    // `.snap-grid--talent` auto-row height). Explicit tracks let collapsed and
    // expanded derivedContexts coexist in one grid.
    const compact = isCompactCatalog(derivedContexts);
    const rows = ['80px'];
    for (const c of visibleComps) {
      if (isCompExpanded(c.id)) {
        for (let i = 0; i < c.skills.length; i += 1) rows.push('38px');
      } else {
        rows.push(`${compRowHeight(c.skills.length, compact)}px`);
      }
    }
    base.gridTemplateRows = rows.join(' ');
    return base;
  }, [peopleSegments, fnCount, visibleComps, derivedContexts, isCompExpanded]);

  // Cumulative left offsets for the four frozen label columns (widths
  // 130/42/6/150 + the 6px grid gap), so they pin to the left edge while
  // the people columns scroll horizontally. Mirrors the leading `cols`
  // literals in `gridStyle`. (Only the first four left edges matter; the
  // skill column's width affects where the scrolling region starts, not
  // these offsets.)
  const freezeLefts = [0, 136, 184, 196];

  // Single-person selection → an individual "Talent Profile" platform map
  // (derivedContexts × functions, grayscale by this engineer's proficiency).
  const profileEngineer =
    personFilter.selected.length === 1
      ? (allEngineers.find((e) => e.id === personFilter.selected[0]) ?? null)
      : null;

  if (profileEngineer) {
    return (
      <section>
        <h1 className='snap-h1'>Talent Profile</h1>
        <p className='snap-subtitle'>
          {`${profileEngineer.display_name}'s proficiency across the platform`}
        </p>
        <FloatingToolbar>
          {personFilter.show ? (
            <ToolbarMultiSelect
              label='Filter by person'
              selected={personFilter.selected}
              options={personFilter.options}
              onChange={personFilter.setSelected}
              allLabel='All people'
            />
          ) : null}
          <ToolbarSeparator />
          <ToolbarToggle
            label='Group Skills'
            pressed={grouped}
            onToggle={() => setGrouped(v => !v)}
          >
            <GroupSkillsIcon />
          </ToolbarToggle>
          <ToolbarToggle
            label='Show Libraries'
            pressed={techFilter.showLibraries}
            onToggle={() => techFilter.setShowLibraries(!techFilter.showLibraries)}
          >
            <ShowLibrariesIcon />
          </ToolbarToggle>
        </FloatingToolbar>
        <TalentProfileMap
          engineer={profileEngineer}
          derivedContexts={derivedContexts}
          businessFunctions={businessFunctions}
          ratings={ratings}
          ownershipIndex={ownershipIndex}
          introductionIndex={introductionIndex}
          grouped={grouped}
          techVisible={techFilter.visible}
        />
      </section>
    );
  }

  return (
    <section>
      <h1 className='snap-h1'>Talent</h1>
      <p className='snap-subtitle'>{SUBTITLE}</p>

      <FloatingToolbar>
        {teamFilter.show ? (
          <ToolbarMultiSelect
            label='Filter by team'
            selected={teamFilter.selected}
            options={teamFilter.options}
            onChange={teamFilter.setSelected}
            allLabel='All teams'
          />
        ) : null}
        {personFilter.show ? (
          <ToolbarMultiSelect
            label='Filter by person'
            selected={personFilter.selected}
            options={personFilter.options}
            onChange={personFilter.setSelected}
            allLabel='All people'
          />
        ) : null}
        {teamFilter.show || personFilter.show ? <ToolbarSeparator /> : null}
        <ToolbarToggle
          label='Group Skills'
          pressed={grouped}
          onToggle={() => setAllGrouped(!grouped)}
        >
          <GroupSkillsIcon />
        </ToolbarToggle>
        <ToolbarToggle
          label='Show Libraries'
          pressed={techFilter.showLibraries}
          onToggle={() => techFilter.setShowLibraries(!techFilter.showLibraries)}
        >
          <ShowLibrariesIcon />
        </ToolbarToggle>
        <ToolbarToggle
          label='Show Departed'
          pressed={showDeparted}
          onToggle={() => setShowDeparted(v => !v)}
        >
          <ShowDepartedIcon />
        </ToolbarToggle>
      </FloatingToolbar>

      <MatrixScrollBox>
      <div className='snap-grid snap-grid--talent' style={gridStyle}>
        <div className='snap-grid__freeze-corner is-frozen' style={{ left: freezeLefts[0] }} />
        <div className='snap-grid__freeze-corner is-frozen' style={{ left: freezeLefts[1] }} />
        <div className='snap-grid__freeze-corner is-frozen' style={{ left: freezeLefts[2] }} />
        <div className='snap-grid__freeze-corner is-frozen is-frozen-edge' style={{ left: freezeLefts[3] }} />
        {peopleSegments.flatMap((seg, segIdx) => {
          const nodes = [];
          if (segIdx > 0) {
            nodes.push(<div key={`gap-h-${segIdx}`} />);
          }
          seg.forEach(p => {
            nodes.push(
              <EngineerColHeader
                key={p.id}
                engineer={p}
                zIndex={colZ.get(p.id) ?? 1}
                onSelect={id => personFilter.setSelected([id])}
              />
            );
          });
          return nodes;
        })}
        <div />{/* trailing filler for header row */}

        {visibleComps.map(comp => {
          if (!isCompExpanded(comp.id)) {
              // Count the derivedContext's underlying skills that are
              // institutional/proprietary so grouped rows can surface a
              // "N proprietary" callout — the grouped view hides the per-
              // skill prop dot, so this is the only place that signal lands.
              const propCount = comp.skills.filter(isInstitutional).length;
              return (
              <Fragment key={comp.id}>
                <GroupedCompLabel
                  frozen
                  freezeLefts={freezeLefts}
                  propCount={propCount}
                  onToggle={() => toggleCompExpanded(comp.id)}
                  expanded={false}
                  name={<TruncatedLabel text={comp.display} max={22} />}
                  count={
                    <>
                      {comp.skills.length} skill
                      {comp.skills.length === 1 ? '' : 's'}
                    </>
                  }
                />
                {peopleSegments.flatMap((seg, segIdx) => {
                  const nodes = [];
                  if (segIdx > 0) {
                    nodes.push(<div key={`gap-${comp.id}-${segIdx}`} />);
                  }
                  seg.forEach(p => {
                    nodes.push(
                      <BestInCompCell
                        key={p.id}
                        engineer={p}
                        comp={comp}
                        ratingIndex={ratingIndex}
                      />
                    );
                  });
                  return nodes;
                })}
                <div key={`filler-${comp.id}`} />{/* trailing filler */}
              </Fragment>
              );
          }
              const multiSkill = comp.skills.length > 1;
              return (
                <Fragment key={comp.id}>
                  {comp.skills.map((skill, i) => (
                    <Fragment key={skill.id}>
                      {/*
                        Ungrouped mode: when the derivedContext has more than
                        one skill, let its label span 2 rows + wrap to 2
                        lines (same pattern Resilience uses). Otherwise
                        keep the single-row truncated label.
                      */}
                      {i === 1 && multiSkill ? null : (
                        <div
                          className={`snap-grid__row-label snap-grid__row-label--muted is-frozen${
                            i === 0 && multiSkill
                              ? ' snap-grid__row-label--wrap-2'
                              : ''
                          }`}
                          style={
                            i === 0 && multiSkill
                              ? { gridRow: 'span 2', left: freezeLefts[0] }
                              : { left: freezeLefts[0] }
                          }
                        >
                          {i === 0 ? (
                            <TruncatedLabel text={comp.display} max={multiSkill ? 60 : 15} />
                          ) : null}
                        </div>
                      )}
                      <ConnectorCell
                        variant={nestVariant(i, comp.skills.length)}
                        onToggle={() => toggleCompExpanded(comp.id)}
                        frozen
                        style={{ left: freezeLefts[1] }}
                      />
                      <div
                        className='snap-grid__row-label snap-grid__row-label--prop is-frozen'
                        style={{ left: freezeLefts[2] }}
                      >
                        <PropIndicator institutional={isInstitutional(skill)} />
                      </div>
                      <div
                        className='snap-grid__row-label is-frozen is-frozen-edge'
                        style={{ left: freezeLefts[3] }}
                      >
                        <TruncatedLabel text={skill.display} />
                      </div>
                    {peopleSegments.flatMap((seg, segIdx) => {
                      const nodes = [];
                      if (segIdx > 0) {
                        nodes.push(<div key={`gap-${skill.id}-${segIdx}`} />);
                      }
                      seg.forEach(p => {
                        const rating =
                          ratingIndex.get(p.id)?.get(skill.id) ?? null;
                        const tooltip =
                          rating === null
                            ? null
                            : {
                                title: `${formatShortName(p.display_name)} — ${RATING_LABELS[rating]}`,
                                body: skill.display,
                                sub: isInstitutional(skill)
                                  ? 'Institutional knowledge'
                                  : undefined,
                              };
                        // `green` opts into the shared talent ramp; `is-agent`
                        // adds the holographic ::before overlay (defined once
                        // in tokens.css so Talent + Resource stay in sync).
                        nodes.push(
                          <MatrixCell
                            key={p.id}
                            value={rating}
                            levelClass={`green ${levelClass(rating)}`}
                            extraClass={isAgent(p) ? 'is-agent' : undefined}
                            tooltip={tooltip}
                          />
                        );
                      });
                      return nodes;
                    })}
                    <div key={`filler-${skill.id}`} />{/* trailing filler */}
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
      </div>
      </MatrixScrollBox>
    </section>
  );
}

/**
 * Grouped-mode cell: an engineer's BEST rating across all visible
 * skills in the derivedContext. Answers "what's their strength in this
 * area?" at a glance. Tooltip lists every rated skill in the comp.
 */
function BestInCompCell({
  engineer,
  comp,
  ratingIndex,
}: {
  engineer: Engineer;
  comp: { display: string; skills: Array<{ id: string; display: string }> };
  ratingIndex: ReturnType<typeof buildRatingIndex>;
}) {
  const rated: Array<{ display: string; rating: RatingValue }> = [];
  let best: RatingValue | null = null;
  for (const skill of comp.skills) {
    const r = ratingIndex.get(engineer.id)?.get(skill.id);
    if (r === undefined) continue;
    rated.push({ display: skill.display, rating: r });
    if (best === null || r > best) best = r;
  }
  const tooltip =
    best === null
      ? null
      : {
          title: `${formatShortName(engineer.display_name)} — best in ${comp.display}: ${RATING_LABELS[best]}`,
          body: rated
            .sort((a, b) => b.rating - a.rating)
            .map(r => `${r.display} — ${RATING_LABELS[r.rating]}`)
            .join('\n'),
        };
  return (
    <MatrixCell
      value={best}
      levelClass={`green ${levelClass(best)}`}
      extraClass={`is-merged${isAgent(engineer) ? ' is-agent' : ''}`}
      tooltip={tooltip}
    />
  );
}
